import React, { useState, useEffect } from "react";
import { FiPlus, FiTrash2, FiEdit2, FiArrowUp, FiArrowDown, FiSave, FiYoutube } from "react-icons/fi";
import Modal from "react-modal";
import Select from "react-select";
import { motion } from "framer-motion";
import { FaAngleDoubleRight } from "react-icons/fa";
import { customSelect } from './Styles/CustomStyles';
import { getOnlyProductNames, getBanners, createBanner, updateBanner, deleteBanner, updateBannerOrder, getCategories, deleteImageFromS3 } from "../services/ClientService";
import { optimizeAndConvertToWebp, uploadImageToS3Bucket } from "../services/imageOptimizerService";
import ConfirmModal from './ConfirmModal'; // Import ConfirmModal

// Selection mode options for product source
const selectionModeOptions = [
  { value: 'manual', label: 'Select Products Manually' },
  { value: 'category', label: 'Select by Category' },
  { value: 'subcategory', label: 'Select by Subcategory' }
];

const BannerCustomization = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [banners, setBanners] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentBannerId, setCurrentBannerId] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Tracks a newly uploaded S3 file that hasn't been saved yet, so it can be deleted if user cancels
  const [pendingUploadFileName, setPendingUploadFileName] = useState(null);
  const [productOptions, setproductOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);

  const [formData, setFormData] = useState({
    image: null,
    imageFileName: null, // S3 fileName for the uploaded image (used for tracking)
    name: "",
    products: [],
    youtubeUrl: "",
    selectionMode: selectionModeOptions[0], // Default to manual
    selectedCategory: null,
    selectedSubcategory: null,
  });

  const [orderChanged, setOrderChanged] = useState(false);

  // Fetch banners, product names, and categories on component mount
  useEffect(() => {
    fetchBanners();
    fetchProductNames();
    fetchCategories();
  }, []);

  // Update subcategories when category changes
  useEffect(() => {
    if (formData.selectedCategory) {
      const category = categoryOptions.find(cat => cat.value === formData.selectedCategory?.value);
      if (category && category.subcategories && category.subcategories.length > 0) {
        const subcats = category.subcategories.map(sub => ({
          value: sub._id,
          label: sub.categoryName
        }));
        setSubcategoryOptions(subcats);
      } else {
        setSubcategoryOptions([]);
      }
    } else {
      setSubcategoryOptions([]);
    }
  }, [formData.selectedCategory, categoryOptions]);

  const fetchBanners = async () => {
    setLoading(true); // Set loading to true at the start
    try {
      const data = await getBanners();
      if (data.success) setBanners(data.data);
    } catch (error) {
      console.error("Error fetching banners:", error);
    } finally {
      setLoading(false); // Set loading to false in finally block
    }
  };

  const fetchProductNames = async () => {
    try {
      const data = await getOnlyProductNames();
      if (data.success) setproductOptions(data.data);
    } catch (error) {
      console.error("Error fetching productsNames:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await getCategories({ includeSubcategories: true });
      if (data.success && data.data) {
        const parentCategories = data.data.map((cat) => ({
          value: cat._id,
          label: cat.categoryName,
          subcategories: cat.subcategories || []
        }));
        setCategoryOptions(parentCategories);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    
    // Clear subcategory when category changes
    if (field === 'selectedCategory') {
      setFormData((prev) => ({ ...prev, [field]: value, selectedSubcategory: null }));
    }
    // Clear other selections when mode changes
    if (field === 'selectionMode') {
      setFormData((prev) => ({ 
        ...prev, 
        [field]: value,
        products: [],
        selectedCategory: null,
        selectedSubcategory: null
      }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        setUploading(true);
        // Optimize and convert image to WebP
        const optimizedFile = await optimizeAndConvertToWebp(file);
        // Generate a unique file name (using same branch convention as gallery)
        const branch = "zun";
        const fileName = `${branch}${Date.now()}.webp`;
        const fileType = "image/webp";
        // Upload the optimized file to S3 bucket
        const publicUrl = await uploadImageToS3Bucket(optimizedFile, fileType, fileName);
        // Update form data with the public URL and fileName for tracking
        handleChange("image", publicUrl);
        handleChange("imageFileName", fileName);
        // Always track newly uploaded file so we can clean up if user cancels without saving
        setPendingUploadFileName(fileName);
      } catch (error) {
        console.error("Image processing error:", error);
      } finally {
        setUploading(false);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.image && !formData.youtubeUrl) newErrors.image = "Banner image or YouTube URL is required";
    if (!formData.name.trim()) newErrors.name = "Banner name is required";
    
    const mode = formData.selectionMode?.value || 'manual';
    if (mode === 'manual' && formData.products.length === 0) {
      newErrors.products = "At least one product must be selected";
    } else if (mode === 'category' && !formData.selectedCategory) {
      newErrors.products = "Please select a category";
    } else if (mode === 'subcategory') {
      if (!formData.selectedCategory) {
        newErrors.products = "Please select a parent category";
      } else if (!formData.selectedSubcategory) {
        newErrors.products = "Please select a subcategory";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      const mode = formData.selectionMode?.value || 'manual';
      const payload = {
        _id: isEditing ? currentBannerId : "",
        bannerName: formData.name,
        imageUrl: formData.image || '',
        youtubeUrl: formData.youtubeUrl || '',
        selectionMode: mode,
        productIds: mode === 'manual' ? formData.products.map((p) => p.value) : [],
        categoryId: formData.selectedCategory ? formData.selectedCategory.value : null,
        subcategoryId: formData.selectedSubcategory ? formData.selectedSubcategory.value : null,
      };
      const data = !isEditing ? await createBanner(payload) : await updateBanner(payload);
      if (data.success) {
        // Banner saved successfully — old image cleanup is handled server-side in updateBanner.
        // Clear pending upload tracking so resetModal won't delete the now-saved file.
        setPendingUploadFileName(null);
        await fetchBanners();
        resetModal(true); // pass true to skip orphan cleanup (already cleared above)
      }
    } catch (error) {
      console.error("Banner operation failed:", error);
      setErrors({ api: error.message || "Operation failed" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRequest = (_id) => {
    setBannerToDelete(_id);
    setConfirmModalVisible(true);
  };

  const confirmDeleteBanner = async () => {
    try {
      setLoading(true);
      const data = await deleteBanner({ _id: bannerToDelete });
      if (data.success) await fetchBanners();
    } catch (error) {
      console.error("Delete failed:", error);
      setErrors({ api: error.message || "Delete failed" });
    } finally {
      setLoading(false);
      setConfirmModalVisible(false);
      setBannerToDelete(null);
    }
  };

  const handleEdit = (banner) => {
    // Determine selection mode from saved banner data
    const savedMode = banner.selectionMode || 'manual';
    const selectionMode = selectionModeOptions.find(m => m.value === savedMode) || selectionModeOptions[0];
    
    // Restore category selection
    let selectedCategory = null;
    if (banner.categoryId) {
      const catIdStr = typeof banner.categoryId === 'object' 
        ? (banner.categoryId.$oid || banner.categoryId.toString()) 
        : String(banner.categoryId);
      selectedCategory = categoryOptions.find(c => String(c.value) === catIdStr) || null;
    }
    
    // Restore subcategory selection
    let selectedSubcategory = null;
    if (banner.subcategoryId && selectedCategory) {
      const subIdStr = typeof banner.subcategoryId === 'object'
        ? (banner.subcategoryId.$oid || banner.subcategoryId.toString())
        : String(banner.subcategoryId);
      const parentCat = categoryOptions.find(c => String(c.value) === String(selectedCategory.value));
      if (parentCat && parentCat.subcategories) {
        const sub = parentCat.subcategories.find(s => String(s._id) === subIdStr);
        if (sub) {
          selectedSubcategory = { value: sub._id, label: sub.categoryName };
        }
      }
    }

    // Extract the existing image fileName from URL for reference
    let existingImageFileName = null;
    if (banner.imageUrl) {
      const parts = banner.imageUrl.split('/');
      existingImageFileName = parts[parts.length - 1] || null;
    }

    setFormData({
      image: banner.imageUrl,
      imageFileName: existingImageFileName,
      name: banner.bannerName,
      products: savedMode === 'manual' ? productOptions.filter((opt) =>
        banner.productIds.includes(opt.value)
      ) : [],
      youtubeUrl: banner.youtubeUrl || '',
      selectionMode: selectionMode,
      selectedCategory: selectedCategory,
      selectedSubcategory: selectedSubcategory,
    });
    // Clear any pending upload tracking from a previous cancelled edit session
    setPendingUploadFileName(null);
    setIsEditing(true);
    setCurrentBannerId(banner._id);
    setIsModalOpen(true);
  };

  const resetModal = (skipCleanup = false) => {
    // If user uploaded a new image but cancelled without saving, remove the orphan from S3
    if (!skipCleanup && pendingUploadFileName) {
      deleteImageFromS3({ fileName: pendingUploadFileName }).catch(err =>
        console.warn('S3 orphan image cleanup error on cancel:', err)
      );
    }
    setPendingUploadFileName(null);
    setIsModalOpen(false);
    setIsEditing(false);
    setCurrentBannerId(null);
    setFormData({ image: null, imageFileName: null, name: "", products: [], youtubeUrl: "", selectionMode: selectionModeOptions[0], selectedCategory: null, selectedSubcategory: null });
    setErrors({});
  };

  // ── Banner Ordering ─────────────────────────────
  const moveBanner = (index, direction) => {
    const newBanners = [...banners];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newBanners.length) return;
    [newBanners[index], newBanners[targetIndex]] = [newBanners[targetIndex], newBanners[index]];
    setBanners(newBanners);
    setOrderChanged(true);
  };

  const handleSaveOrder = async () => {
    try {
      setLoading(true);
      const bannerOrder = banners.map((b, idx) => ({ _id: b._id, sortOrder: idx }));
      const data = await updateBannerOrder({ bannerOrder });
      if (data.success) {
        setOrderChanged(false);
        await fetchBanners();
      }
    } catch (error) {
      console.error("Save order failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-full p-6">
      <div className="flex items-center space-x-2 p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-tl-lg rounded-tr-lg shadow-md border border-gray-200 border-b-0">
        <h2 className="text-xl font-bold text-gray-700 hover:text-purple-600 transition duration-300">Add Banners</h2>
        <FaAngleDoubleRight className="text-purple-500 text-2xl mt-[6px]" />
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="relative flex items-center justify-center w-12 h-12 mt-[6px] bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800 text-white rounded-full shadow-lg focus:outline-none focus:ring-4 focus:ring-purple-400"
          disabled={loading}
        >
          <FiPlus className="text-3xl font-bold animate-smooth-rotate" />
        </button>
        {orderChanged && (
          <button
            onClick={handleSaveOrder}
            className="flex items-center gap-2 ml-auto px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg transition-all font-semibold text-sm"
            disabled={loading}
          >
            <FiSave /> Save Order
          </button>
        )}
      </div>

      {errors.api && Array.isArray(errors.api) && errors.api.map((error, index) => (
        <div key={index} className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mx-4">
          {error}
        </div>
      ))}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 bg-white p-4 rounded-bl-lg rounded-br-lg shadow-lg border border-gray-200">
        {loading ? (
          <div className="absolute inset-0 flex justify-center items-center bg-white/80 rounded-xl">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          banners.map((banner, index) => (
            <motion.div
              key={banner._id}
              className="bg-white rounded-xl shadow-md overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="relative group">
                {banner.imageUrl ? (
                  <img
                    src={banner.imageUrl}
                    alt="Banner"
                    className="w-full h-48 object-cover"
                  />
                ) : banner.youtubeUrl ? (
                  <div className="w-full h-48 bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
                    <FiYoutube className="text-white text-5xl" />
                    <span className="text-white text-sm ml-2 font-medium">YouTube Video</span>
                  </div>
                ) : (
                  <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-400">No image</span>
                  </div>
                )}
                {/* Order badge */}
                <div className="absolute top-2 left-2 bg-black/70 text-white text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center">
                  {index + 1}
                </div>
                {/* YouTube badge */}
                {banner.youtubeUrl && (
                  <div className="absolute bottom-2 left-2 bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1">
                    <FiYoutube size={12} /> Video
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-2">
                  {/* Order controls */}
                  <button
                    onClick={() => moveBanner(index, -1)}
                    className="p-2 bg-white/90 rounded-lg shadow hover:bg-gray-200 disabled:opacity-30"
                    disabled={index === 0 || loading}
                    title="Move Up"
                  >
                    <FiArrowUp className="text-gray-700" />
                  </button>
                  <button
                    onClick={() => moveBanner(index, 1)}
                    className="p-2 bg-white/90 rounded-lg shadow hover:bg-gray-200 disabled:opacity-30"
                    disabled={index === banners.length - 1 || loading}
                    title="Move Down"
                  >
                    <FiArrowDown className="text-gray-700" />
                  </button>
                  <button
                    onClick={() => handleEdit(banner)}
                    className="p-2 bg-white/90 rounded-lg shadow hover:bg-gray-200"
                    disabled={loading}
                  >
                    <FiEdit2 className="text-blue-600" />
                  </button>
                  <button
                    onClick={() => handleDeleteRequest(banner._id)}
                    className="p-2 bg-white/90 rounded-lg shadow hover:bg-gray-200"
                    disabled={loading}
                  >
                    <FiTrash2 className="text-red-600" />
                  </button>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold mb-2">{banner.bannerName}</h3>
                <div className="space-y-2 overflow-y-scroll max-h-44 scrollbar-hide">
                  {banner.productIds.map((productId) => {
                    const product = productOptions.find(opt => opt.value === productId);
                    return (
                      <div key={productId} className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                        <div>
                          <p className="text-sm font-medium">{product?.label}</p>
                          <p className="text-xs text-gray-500">{product?.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={resetModal}
        shouldCloseOnOverlayClick={false}  // Prevent modal from closing automatically
        className="bg-white rounded-2xl shadow-2xl p-6 md:w-[50%] w-[90%] max-h-[90vh] overflow-y-auto"
        overlayClassName="fixed inset-0 bg-black/50 flex items-center justify-center"
        style={{
          content: {
            maxHeight: '90vh',
            overflowY: 'auto',
          },
        }}
      >
        <h2 className="text-2xl font-bold mb-6">
          {isEditing ? "Edit Banner" : "Create New Banner"}
        </h2>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">Banner Image</label>
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-purple-500 transition-colors">
              <input
                type="file"
                onChange={handleImageUpload}
                className="hidden"
                id="banner-upload"
                accept="image/*"
              />
              <label htmlFor="banner-upload" className="cursor-pointer block">
                {uploading ? (
                  <div className="absolute inset-0 flex justify-center items-center bg-white/80 rounded-xl">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : formData.image ? (
                  <img
                    src={formData.image}
                    alt="Preview"
                    className="max-h-48 mx-auto rounded-lg"
                  />
                ) : (
                  <div className="py-8">
                    <p className="text-gray-500 mb-2">Click to upload image</p>
                    <p className="text-xs text-gray-400">Recommended size: 1200x400px</p>
                  </div>
                )}
              </label>
            </div>
            {errors.image && <p className="text-red-500 text-sm mt-1">{errors.image}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Banner Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Summer Sale Banner"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">YouTube URL <span className="text-gray-400 text-xs">(optional - for video banners)</span></label>
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-lg shrink-0">
                <FiYoutube className="text-red-600 text-xl" />
              </div>
              <input
                type="url"
                value={formData.youtubeUrl}
                onChange={(e) => handleChange("youtubeUrl", e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-400 focus:border-transparent"
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">Add a YouTube video URL. The banner will show a video icon on the mobile app.</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Select Products *</label>
            
            {/* Selection Mode Toggle */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">How do you want to add products?</label>
              <Select
                options={selectionModeOptions}
                value={formData.selectionMode}
                onChange={(option) => handleChange('selectionMode', option)}
                styles={customSelect}
                className="react-select-container"
                classNamePrefix="react-select"
                isSearchable={false}
              />
            </div>

            {/* Manual Product Selection */}
            {formData.selectionMode?.value === 'manual' && (
              <Select
                isMulti
                closeMenuOnSelect={false}
                options={productOptions}
                value={formData.products}
                onChange={(selected) => handleChange("products", selected)}
                styles={{
                  ...customSelect,
                  valueContainer: (provided) => ({
                    ...provided,
                    maxHeight: "100px",
                    overflowY: "auto"
                  })
                }}
                className="react-select-container scrollbar-thin"
                classNamePrefix="react-select"
                placeholder="Search Products"
                menuPlacement="top"
              />
            )}

            {/* Category Selection */}
            {formData.selectionMode?.value === 'category' && (
              <div>
                <Select
                  options={categoryOptions}
                  value={formData.selectedCategory}
                  onChange={(option) => handleChange('selectedCategory', option)}
                  styles={customSelect}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isClearable
                  placeholder="Select a category..."
                  menuPlacement="top"
                />
                {formData.selectedCategory && (
                  <p className="mt-1 text-xs text-gray-500">
                    All products from "{formData.selectedCategory.label}" will be linked to this banner
                  </p>
                )}
              </div>
            )}

            {/* Subcategory Selection */}
            {formData.selectionMode?.value === 'subcategory' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Select Parent Category</label>
                  <Select
                    options={categoryOptions}
                    value={formData.selectedCategory}
                    onChange={(option) => handleChange('selectedCategory', option)}
                    styles={customSelect}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isClearable
                    placeholder="First, select a parent category..."
                    menuPlacement="top"
                  />
                </div>
                {formData.selectedCategory && subcategoryOptions.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Select Subcategory</label>
                    <Select
                      options={subcategoryOptions}
                      value={formData.selectedSubcategory}
                      onChange={(option) => handleChange('selectedSubcategory', option)}
                      styles={customSelect}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      isClearable
                      placeholder="Select a subcategory..."
                      menuPlacement="top"
                    />
                    {formData.selectedSubcategory && (
                      <p className="mt-1 text-xs text-gray-500">
                        All products from "{formData.selectedSubcategory.label}" will be linked to this banner
                      </p>
                    )}
                  </div>
                )}
                {formData.selectedCategory && subcategoryOptions.length === 0 && (
                  <p className="text-sm text-amber-600">
                    This category has no subcategories. Please select a different category or use the Category selection mode.
                  </p>
                )}
              </div>
            )}

            {errors.products && <p className="text-red-500 text-sm mt-1">{errors.products}</p>}
          </div>

          <div className="flex gap-4 justify-between">
            <button
              type="button"
              onClick={resetModal}
              className="bg-gray-500 text-white px-4 py-2 rounded-full hover:bg-gray-700 w-1/3 max-md:w-1/2"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="bg-purple-500 text-white px-4 py-2 rounded-full hover:bg-purple-700 w-1/3 max-md:w-1/2 relative"
              disabled={loading}
            >
              {loading && (
                <span className="absolute left-0 top-0 w-full h-full flex items-center justify-center">
                  <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
                </span>
              )}
              <span className={loading ? "invisible" : ""}>
                {isEditing ? "Update Banner" : "Create Banner"}
              </span>
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        visible={confirmModalVisible}
        message="Are you sure you want to delete this banner?"
        onConfirm={confirmDeleteBanner}
        onCancel={() => {
          setConfirmModalVisible(false);
          setBannerToDelete(null);
        }}
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
      />
    </div>
  );
};

export default BannerCustomization;